import { useState, useEffect, useMemo } from 'react';
import * as breakpoints from '@Styles/mixins/breakpoints.module.scss';

/**
 * Custom hook for responsive behavior based on window width
 * @param {string} breakpoint - Breakpoint name ('mobile', 'tablet', 'desktop', 'wide') (default: 'tablet')
 * @returns {boolean} - Whether the current window width is above the breakpoint
 */
const useBreakpoint = (breakpoint = 'tablet') => {
  const breakpointValue = useMemo(() => {
    const value = breakpoints[breakpoint];
    if (!value) {
      return parseInt(breakpoints.tablet, 10);
    }
    return parseInt(value, 10);
  }, [breakpoint]);

  // Memoize the media query string to avoid recreating it
  const mediaQuery = useMemo(() => `(min-width: ${breakpointValue}px)`, [breakpointValue]);

  const [isBreakpoint, setIsBreakpoint] = useState(() => {
    if (typeof window === 'undefined') return true;
    const media = window.matchMedia(mediaQuery);
    return media?.matches || false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const media = window.matchMedia(mediaQuery);

    if (!media) return undefined;

    setIsBreakpoint(media.matches || false);

    const handleChange = (event) => setIsBreakpoint(event?.matches || false);

    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [mediaQuery]);

  return isBreakpoint;
};

export default useBreakpoint;
